import React, { ForwardedRef, ReactElement, ReactNode, useState } from "react";
import { Box, Button, ExpandIcon, Spinner } from "@src/components/elements";
import styled from "styled-components/macro";

type Props = {
  titleBar?: ReactElement;
  noBorder?: boolean;
  children?: ReactNode;
  className?: string;
  hideOverflow?: boolean;
  variant?: "primary" | "secondary" | undefined;
  loading?: boolean;
  expanded?: boolean;
};

const StyledToggleRoot = styled.div<Props & { expanded: boolean }>`
  --toggle-width: 2rem;

  .box {
    background-color: ${({ variant }) =>
      variant === "secondary" ? "var(--background-gray)" : "var(--white)"};
    display: grid;
    grid-template-columns: ${({ variant }) =>
      variant === "secondary"
        ? "auto var(--toggle-width)"
        : "var(--toggle-width) auto"};
    grid-template-rows: ${({ expanded }) => (expanded ? "auto auto" : "1fr")};
    grid-template-areas: ${({ variant }) =>
      variant === "secondary"
        ? `
        "title toggle"
        "content content"
        `
        : `
        "toggle title"
        "content content"
    `};
    ${(props) => (props.noBorder ? "border: none;" : "")}
    padding: ${({ variant }) =>
      variant === "secondary" ? "1rem" : "1rem 1rem 1rem 0.5rem"};
  }

  h3 {
    margin: 0.5rem 0;
  }

  h5 {
    font-weight: var(--font-weight-bold);
  }

  .toggle {
    grid-area: toggle;
    width: var(--toggle-width);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .expander {
    ${({ variant }) =>
      variant === "secondary" ? undefined : "height: 1.25rem;"}
    fill: var(--dark-gray);
  }

  .title {
    grid-area: title;
  }

  .content {
    grid-area: content;
    overflow: auto;
    margin-left: 2rem;
    .children {
      flex-grow: 1;
      ${({ hideOverflow }) =>
        hideOverflow ? "overflow: hidden;" : "overflow-x: auto;"}
    }
  }
  .table-wrap {
    width: 100%;
  }
`;

export const TogglePanel = React.forwardRef(
  (
    {
      titleBar,
      children,
      noBorder,
      className,
      hideOverflow,
      variant,
      loading,
      expanded: initialExpanded = true,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const [expanded, setExpanded] = useState(initialExpanded);
    const onClickExpander = () => {
      setExpanded(!expanded);
    };

    return (
      <StyledToggleRoot
        className={className}
        noBorder={noBorder}
        hideOverflow={hideOverflow}
        variant={variant}
        expanded={expanded}
      >
        <Box ref={ref} className="box">
          {loading && (
            <div className="row center">
              <Spinner />
            </div>
          )}
          {!loading && (
            <>
              <div className="toggle">
                {variant === "secondary" ? (
                  <Button
                    variant="tertiary"
                    onClick={onClickExpander}
                    size="small"
                  >
                    <ExpandIcon className="expander" expanded={expanded} />
                  </Button>
                ) : (
                  <ExpandIcon
                    className="expander"
                    expanded={expanded}
                    onClick={onClickExpander}
                  />
                )}
              </div>
              <div className="title">{titleBar}</div>
              <div className="content">
                {expanded && <div className="children">{children}</div>}
              </div>
            </>
          )}
        </Box>
      </StyledToggleRoot>
    );
  },
);

TogglePanel.displayName = "TogglePanel";
